import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {
  type CompanyDetailsStepForm,
  companyDetailsStepSchema,
  type ExistingAccountStepForm,
  existingAccountStepSchema,
  type OwnerAccountStepForm,
  ownerAccountStepSchema,
} from '@willnq/shared/validation/companyRegistrationSchemas';
import {
  COUNTRIES,
  DEFAULT_COUNTRY_NAME,
  defaultTimezoneForCountry,
  isTimezoneValidForCountry,
  timezonesForCountry,
} from '@willnq/shared/validation/countries';
import { useEffect, useState } from 'react';
import {
  type Control,
  Controller,
  useForm,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { PasswordField, SelectField, TextField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { COMPANY_SIZE_OPTIONS } from '@/features/company/constants';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { PasswordRequirements } from '@/features/identity/components/PasswordRequirements';

import { CompanyTermsLinks } from '../components/CompanyTermsLinks';
import { VERIFICATION_WINDOW_HOURS } from '../constants';
import { useCompanyRegistrationMutations } from '../hooks/useCompanyRegistrationMutations';

const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({ value: country.name, label: country.name }));

const DEFAULT_TIMEZONE = defaultTimezoneForCountry(DEFAULT_COUNTRY_NAME) ?? 'Asia/Kolkata';

/**
 * Step 1 is the same whoever is registering; only the applicant step differs by journey.
 *
 * Country and time zone are one question, not two. The country is chosen; the zone follows from it —
 * derived silently where a country has only one (the common case, India included), and asked only
 * where a country genuinely splits, from that country's zones alone. The previous form offered the
 * runtime's entire IANA list next to a free-text country, which asked 400+ questions to decorate one
 * row of the reviewer's dialog and let "India" be paired with any clock on earth.
 */
function CompanyDetailsFields({ control }: { control: Control<CompanyDetailsStepForm> }) {
  const country = useWatch({ control, name: 'country' });
  const zones = timezonesForCountry(country);
  const needsChoice = zones.length > 1;

  return (
    <>
      <TextField name="companyName" control={control} label="Company name" />
      <TextField
        name="companyCode"
        control={control}
        label="Company code"
        helperText="A short unique identifier, e.g. ACME"
      />
      <TextField name="industry" control={control} label="Industry (optional)" />
      <SelectField name="size" control={control} label="Company size" options={COMPANY_SIZE_OPTIONS} />
      <TextField name="website" control={control} label="Website (optional)" />
      <SelectField
        name="country"
        control={control}
        label="Country"
        options={COUNTRY_OPTIONS}
        // Where the zone is derived, say which one: it is stored on the registration and shown to the
        // reviewer, so it should not be a value the founder never saw.
        helperText={needsChoice ? undefined : (zones[0]?.label ?? undefined)}
      />
      {needsChoice ? (
        <SelectField
          name="timezone"
          control={control}
          label="Time zone"
          options={zones.map((zone) => ({ value: zone.id, label: zone.label }))}
          helperText={`${country} spans several time zones, so choose the company's own.`}
        />
      ) : null}
    </>
  );
}

/**
 * Keeps the derived time zone in step with the country. Runs on change rather than at submit so the
 * stored value is never a leftover from a previously chosen country, and so a multi-zone country
 * arrives pre-filled with its most populous zone instead of an empty required field.
 *
 * Skipped when the current zone is already valid for the country, which is what makes it safe for the
 * multi-zone case: it seeds a default without overwriting a deliberate choice on every re-render.
 */
function useDerivedTimezone(form: UseFormReturn<CompanyDetailsStepForm>) {
  const country = useWatch({ control: form.control, name: 'country' });

  useEffect(() => {
    const fallback = defaultTimezoneForCountry(country);
    if (!fallback) return; // a country outside the list — the schema reports it; do not invent a zone
    if (isTimezoneValidForCountry(country, form.getValues('timezone'))) return;
    form.setValue('timezone', fallback, { shouldValidate: form.formState.isSubmitted });
  }, [country, form]);
}

/** The terms checkbox, identical on both journeys — a per-registration legal acceptance. */
function AcceptTermsField({
  control,
}: {
  control: Control<OwnerAccountStepForm> | Control<ExistingAccountStepForm>;
}) {
  return (
    <Controller
      name="acceptTerms"
      // One cast rather than a generic component: both journeys' step-2 forms declare `acceptTerms`
      // as a boolean at this exact path, so the narrowing is safe, and RHF's `Control` is invariant
      // in its form type — a generic signature here buys nothing but noise at every call site.
      control={control as Control<ExistingAccountStepForm>}
      render={({ field, fieldState }) => (
        <FormControl error={Boolean(fieldState.error)}>
          <FormControlLabel
            control={
              <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
            }
            label={
              <>
                I agree to the <CompanyTermsLinks /> on behalf of this company, and confirm I am
                authorised to do so.
              </>
            }
          />
          {fieldState.error ? <FormHelperText>{fieldState.error.message}</FormHelperText> : null}
        </FormControl>
      )}
    />
  );
}

/**
 * The company-registration wizard (CompanyRegistration.md §3), serving both onboarding journeys from
 * one page. Step 1 collects the company that will be born at bootstrap. Step 2 depends on who is
 * asking:
 *
 *  - **Anonymous** — collects the applicant account to create, then sends a verification code to the
 *    corporate email and continues at `/register-company/verify`.
 *  - **Signed in** — reuses the authenticated Account as the applicant. No email, no password, no
 *    second account, no verification step; the registration is already awaiting approval, so this
 *    lands straight in the Company workspace on its waiting screen.
 *
 * Submission creates a CompanyRegistration, never a Company.
 */
export function RegisterCompanyPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { submitRegistration, submitting } = useCompanyRegistrationMutations();
  const [step, setStep] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);

  const companyForm = useForm<CompanyDetailsStepForm>({
    resolver: zodResolver(companyDetailsStepSchema),
    defaultValues: {
      companyName: '',
      companyCode: '',
      industry: '',
      size: 'SMALL',
      website: '',
      // India, not the browser's locale: a laptop set to another zone should not quietly register a
      // company into it, and this is an India-first platform.
      country: DEFAULT_COUNTRY_NAME,
      timezone: DEFAULT_TIMEZONE,
    },
  });

  useDerivedTimezone(companyForm);

  // Both applicant-step forms are created unconditionally so the hook order never changes between
  // renders; only one of them is ever rendered. An RHF `Controller` must not have its `control`
  // swapped in place, which is also why the journey is resolved (below) before any of this renders.
  const ownerForm = useForm<OwnerAccountStepForm>({
    resolver: zodResolver(ownerAccountStepSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
  });

  const existingAccountForm = useForm<ExistingAccountStepForm>({
    resolver: zodResolver(existingAccountStepSchema),
    defaultValues: { acceptTerms: false },
  });

  const password = ownerForm.watch('password');

  const goToApplicantStep = companyForm.handleSubmit(() => setStep(1));

  /**
   * Maps the server's duplicate guards back onto their fields (uniqueness is server-side only —
   * §8.1), falling back to a page-level alert for anything unmapped.
   */
  const showErrors = (
    errors: { code: string; message: string }[],
    emailField: (message: string) => void,
  ) => {
    let mapped = false;
    for (const error of errors) {
      if (error.code === 'CompanyRegistration.CompanyNameTaken') {
        companyForm.setError('companyName', { message: error.message });
        setStep(0);
        mapped = true;
      } else if (error.code === 'CompanyRegistration.CompanyCodeTaken') {
        companyForm.setError('companyCode', { message: error.message });
        setStep(0);
        mapped = true;
      } else if (
        error.code === 'CompanyRegistration.EmailAlreadyInUse' ||
        error.code === 'CompanyRegistration.DuplicateRegistration'
      ) {
        emailField(error.message);
        mapped = true;
      }
    }
    if (!mapped) {
      setServerError(errors[0]?.message ?? 'Could not submit the registration. Please try again.');
    }
  };

  const submitAsNewAccount = ownerForm.handleSubmit(async (owner) => {
    setServerError(null);
    const company = companyForm.getValues();
    const outcome = await submitRegistration({
      companyName: company.companyName,
      companyCode: company.companyCode,
      industry: company.industry || null,
      size: company.size,
      website: company.website || null,
      country: company.country,
      timezone: company.timezone,
      firstName: owner.firstName,
      lastName: owner.lastName,
      email: owner.email,
      password: owner.password,
      acceptTerms: owner.acceptTerms,
    });

    if (outcome.ok && outcome.registration) {
      // `id` goes in the URL (not just router state) so the verify step survives a reload, a
      // closed tab, or the applicant bookmarking/re-visiting the link later — state alone is lost
      // the moment the tab navigates away, stranding the registration in PendingEmailVerification
      // with no way back to enter or resend the OTP.
      navigate(`${ROUTES.registerCompanyVerify}?id=${outcome.registration.id}`, {
        state: { email: outcome.registration.corporateEmail },
      });
      return;
    }

    showErrors(outcome.errors, (message) => ownerForm.setError('email', { message }));
  });

  const submitAsExistingAccount = existingAccountForm.handleSubmit(async (confirmation) => {
    setServerError(null);
    const company = companyForm.getValues();
    // The applicant fields are deliberately absent: the server takes the applicant from the session,
    // and sending them while signed in is rejected rather than ignored.
    const outcome = await submitRegistration({
      companyName: company.companyName,
      companyCode: company.companyCode,
      industry: company.industry || null,
      size: company.size,
      website: company.website || null,
      country: company.country,
      timezone: company.timezone,
      acceptTerms: confirmation.acceptTerms,
    });

    if (outcome.ok && outcome.registration) {
      // Already PendingApproval — there is no verification step to visit. The Company Portal gate
      // reads `myCompanyRegistration` (refetched by the mutation), so this resolves to the
      // awaiting-approval screen rather than bouncing back to onboarding.
      navigate(ROUTES.company, { replace: true });
      return;
    }

    showErrors(outcome.errors, (message) => setServerError(message));
  });

  // The journey is decided by the session, so nothing renders until the silent session restore has
  // resolved: showing the anonymous wizard first and swapping it out would reset a half-filled form.
  if (isLoading) {
    return (
      <AuthCard>
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </AuthCard>
    );
  }

  const steps = isAuthenticated ? (['Company', 'Confirm'] as const) : (['Company', 'Owner account'] as const);

  return (
    <AuthCard
      footer={
        isAuthenticated ? undefined : (
          <>
            Already have an account? <RouterLink to={ROUTES.login}>Sign in</RouterLink>
          </>
        )
      }
    >
      <AuthHeader
        title="Register your company"
        subtitle="Tell us about your company. Registrations are reviewed before activation."
      />
      <Stepper activeStep={step} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {serverError ? <Alert severity="error">{serverError}</Alert> : null}

      {/* Each step's form is keyed: without keys React reuses the step-1 field instances for
          step 2 (same component types at the same positions), and an RHF Controller does not
          support having its `control`/`name` swapped in place — the reused fields stay bound to
          stale form state and silently drop input. */}
      {step === 0 ? (
        <Stack
          key="company-step"
          component="form"
          onSubmit={(e) => void goToApplicantStep(e)}
          spacing={2}
          noValidate
        >
          <CompanyDetailsFields control={companyForm.control} />
          <Button type="submit" variant="contained" size="large">
            Next
          </Button>
        </Stack>
      ) : isAuthenticated ? (
        <Stack
          key="existing-account-step"
          component="form"
          onSubmit={(e) => void submitAsExistingAccount(e)}
          spacing={2}
          noValidate
        >
          <Alert severity="info" icon={false}>
            <Typography variant="body2">
              You are registering this company using your account
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, wordBreak: 'break-all' }}>
              {user?.email}
            </Typography>
          </Alert>
          <Box>
            <Typography variant="body2" color="text.secondary">
              You will become the founding Owner of this company once the registration is approved.
              Your existing account is used, so there is nothing new to set up.
            </Typography>
          </Box>
          <AcceptTermsField control={existingAccountForm.control} />
          <Stack direction="row" spacing={2}>
            <Button onClick={() => setStep(0)} disabled={submitting} fullWidth>
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Register company
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack
          key="owner-step"
          component="form"
          onSubmit={(e) => void submitAsNewAccount(e)}
          spacing={2}
          noValidate
        >
          <Stack direction="row" spacing={2}>
            <TextField name="firstName" control={ownerForm.control} label="First name" />
            <TextField name="lastName" control={ownerForm.control} label="Last name" />
          </Stack>
          {/* No native type="email" — Zod owns validation so messages are consistent (§8.1). */}
          <TextField name="email" control={ownerForm.control} label="Corporate email" />
          <PasswordField
            name="password"
            control={ownerForm.control}
            label="Password"
            autoComplete="new-password"
          />
          <PasswordRequirements password={password} />
          <PasswordField
            name="confirmPassword"
            control={ownerForm.control}
            label="Confirm password"
            autoComplete="new-password"
          />
          {/* Stated BEFORE submitting, not after. The registration reserves this company name and is
              then deleted if the email is never verified — someone who learns that only from a
              post-hoc email has already had it happen to them. Only this journey needs it: a signed-in
              applicant never enters verification at all. */}
          <Alert severity="warning" icon={false}>
            <Typography variant="body2">
              We&apos;ll email a code to your corporate address. If it isn&apos;t verified within{' '}
              <strong>{VERIFICATION_WINDOW_HOURS} hours</strong>, this registration and the details you
              entered are deleted, the company name is released, and you&apos;d need to start again.
            </Typography>
          </Alert>
          <AcceptTermsField control={ownerForm.control} />
          <Stack direction="row" spacing={2}>
            <Button onClick={() => setStep(0)} disabled={submitting} fullWidth>
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Register company
            </Button>
          </Stack>
        </Stack>
      )}
    </AuthCard>
  );
}
